import {
	PDF_WATERMARK_IMAGE_MAX_BYTES,
	PdfWatermarkImageMimeType,
	PdfWatermarkMode,
	PdfWatermarkPosition,
	type PdfWatermarkSettings,
	type PdfWatermarkSettingsRequest,
} from '@today/types'

// The PDF watermark form's local state and its conversions (TO-495 REQ-011).

/** API field path → message, shown next to that field. */
export type PdfWatermarkFieldErrors = Readonly<Record<string, string>>

export interface PickedImage {
	mimeType: PdfWatermarkImageMimeType
	contentBase64: string
}

export interface PdfWatermarkDraft {
	mode: PdfWatermarkMode
	template: string
	diagonalStamp: boolean
	image: PickedImage | null
	opacity: string
	position: PdfWatermarkPosition
}

export const ACCEPTED_IMAGE_TYPES: readonly string[] = Object.values(PdfWatermarkImageMimeType)

export const draftOf = (settings: PdfWatermarkSettings): PdfWatermarkDraft => ({
	mode: settings.mode,
	template: settings.text?.template ?? '',
	diagonalStamp: settings.text?.diagonalStamp ?? false,
	image: settings.image ? { mimeType: settings.image.mimeType, contentBase64: settings.image.contentBase64 } : null,
	opacity: String(settings.image?.opacity ?? 0.2),
	position: settings.image?.position ?? PdfWatermarkPosition.CENTER,
})

/** The whole replacement the draft stands for, or `null` when image mode has no image yet. */
export const submissionOf = (draft: PdfWatermarkDraft): PdfWatermarkSettingsRequest | null => {
	if (draft.mode === PdfWatermarkMode.TEXT) {
		return { mode: draft.mode, text: { template: draft.template, diagonalStamp: draft.diagonalStamp } }
	}
	if (draft.mode === PdfWatermarkMode.IMAGE) {
		if (!draft.image) return null
		return { mode: draft.mode, image: { ...draft.image, opacity: Number(draft.opacity), position: draft.position } }
	}
	return { mode: PdfWatermarkMode.DISABLED }
}

/** Why a picked file cannot be the watermark — checked before any request — or `null`. */
export const imageFileError = (file: File): string | null => {
	if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) return 'The image must be a PNG or a JPEG'
	if (file.size > PDF_WATERMARK_IMAGE_MAX_BYTES) return 'The image must be 256 KB or smaller'
	return null
}

/** Reads a picked file as base64 (the part after the `data:` URI's comma). */
export const readBase64 = (file: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '')
		reader.onerror = () => reject(reader.error)
		reader.readAsDataURL(file)
	})

/** The image a picked file stands for, or the reason it is refused (and no image). */
export const pickImage = async (file: File): Promise<{ image: PickedImage | null; error: string | null }> => {
	const error = imageFileError(file)
	if (error) return { image: null, error }
	return { image: { mimeType: file.type as PdfWatermarkImageMimeType, contentBase64: await readBase64(file) }, error }
}
